/**
 * AdminStudentDetailPage.jsx  —  /admin/students/:userId
 *
 * Tam sayfa ogrenci detay gorünümü.
 * Layout (yukaridan asagiya):
 *   1. Ust bar: arama input'u + secili ogrenci adi
 *   2. Sekme bari (pill + sayac): 9 sekme
 *   3. Sekme icerigi (tam genislik)
 *   4. Alt sabit aksiyon seridi: SMS | PDF | Impersonate | Sertifikalar
 *
 * Mevcut AdminStudentHub sekme bilesenleri aynen yeniden kullanilir —
 * mantik kopyalanmaz.
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ShoppingCart,
  MessageSquare,
  FileText,
  Award,
  BookOpen,
  RefreshCw,
  Search,
  X,
  User,
} from 'lucide-react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { getApiErrorMessage, getApiErrorCode } from '../utils/apiError';
import {
  StatusBadge,
  EmptyState,
  LoadingState,
  ConfirmProvider,
  useConfirmDialog,
  ActionMenu,
} from '../components/admin';
import SmsSendModal from '../components/admin/SmsSendModal';

import {
  useStudent,
  useUpdateStudent,
  useDeleteStudent,
  useCancelDeletion,
  useAdminEnrollDelete,
  useAdminEnrollComplete,
} from '../api/queries/students';

import StudentProfileTab from './AdminStudentHub/StudentProfileTab';
import StudentEnrollmentsTab from './AdminStudentHub/StudentEnrollmentsTab';
import StudentCertificatesTab from './AdminStudentHub/StudentCertificatesTab';
import StudentActivityTab from './AdminStudentHub/StudentActivityTab';
import AccountingTab from './AdminStudentHub/AccountingTab';
import StudentKvkkTab from './AdminStudentHub/StudentKvkkTab';
import StudentSecurityTab from './AdminStudentHub/StudentSecurityTab';
import StudentGorusmelerTab from './AdminStudentHub/StudentGorusmelerTab';
import StudentSinavTab from './AdminStudentHub/StudentSinavTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sekme tanimlamalari
const TABS = [
  { key: 'tab_kimlik', countField: null },
  { key: 'tab_enrollments', countField: 'egitimler' },
  { key: 'tab_payments', countField: 'muhasebe' },
  { key: 'tab_activity', countField: null },
  { key: 'tab_certificates', countField: null },
  { key: 'tab_gorusmeler', countField: 'gorusmeler' },
  { key: 'tab_sinav', countField: 'sinav' },
  { key: 'tab_kvkk', countField: null },
  { key: 'tab_security', countField: null },
];

// ── Arama dropdown'u ──────────────────────────────────────────────────────────
function StudentSearchBar({ currentUserId, onSelect }) {
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const timerRef = useRef(null);

  const search = useCallback(async (q) => {
    if (!q.trim()) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ search: q, limit: '10' });
      const res = await axios.get(`${API}/admin/users?${params}`, { headers: authHeaders() });
      setResults(res.data.users || []);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  const handleChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(v), 280);
  };

  const handleSelect = (u) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    onSelect(u.user_id);
  };

  // Dis tiklamada kapat
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative flex-1 max-w-xs">
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white focus-within:border-[var(--brand-primary)] transition-colors">
        <Search size={15} className="text-[var(--text-muted)] flex-shrink-0" />
        <input
          type="text"
          value={query}
          onChange={handleChange}
          placeholder={t('admin.studentDetail.search_placeholder')}
          className="flex-1 text-sm font-semibold bg-transparent outline-none placeholder:text-[var(--text-muted)] placeholder:font-normal"
          data-testid="student-detail-search"
        />
        {(query || loading) && (
          <button
            type="button"
            onClick={() => { setQuery(''); setResults([]); setOpen(false); }}
            className="text-[var(--text-muted)] hover:text-[var(--text-main)]"
          >
            {loading
              ? <div className="w-3.5 h-3.5 border-2 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin" />
              : <X size={13} />
            }
          </button>
        )}
      </div>

      <AnimatePresence>
        {open && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.1 }}
            className="absolute z-50 top-full mt-1 left-0 right-0 bg-white rounded-xl border-2 border-[var(--border-light)] shadow-lg overflow-hidden max-h-64 overflow-y-auto"
          >
            {results.map((u) => (
              <button
                key={u.user_id}
                type="button"
                onClick={() => handleSelect(u)}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-2 hover:bg-gray-50 transition-colors ${
                  u.user_id === currentUserId ? 'bg-[var(--brand-primary)]/5' : ''
                }`}
              >
                <div className="w-7 h-7 rounded-full bg-[var(--brand-primary)] flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {u.picture
                    ? <img src={u.picture} alt="" className="w-full h-full object-cover" />
                    : <span className="text-white text-[10px] font-bold">{(u.name || u.email || '?')[0].toUpperCase()}</span>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-xs truncate">{u.name || '-'}</p>
                  <p className="text-[10px] text-[var(--text-muted)] truncate">{u.email}</p>
                </div>
                {u.user_id === currentUserId && (
                  <span className="text-[9px] font-bold text-[var(--brand-primary)] bg-[var(--brand-primary)]/10 px-1.5 py-0.5 rounded-full">
                    {lang === 'tr' ? 'Seçili' : 'Active'}
                  </span>
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Ana sayfa inner (ConfirmProvider gerektirir) ───────────────────────────────
function StudentDetailInner() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const confirm = useConfirmDialog();

  const [activeTab, setActiveTab] = useState('tab_kimlik');

  // ── Detail data ──────────────────────────────────────────────────────────────
  const { data: detail, isLoading: detailLoading, refetch: refetchDetail } = useStudent(userId);

  // ── Mutations ────────────────────────────────────────────────────────────────
  const updateStudent = useUpdateStudent(userId);
  const deleteStudent = useDeleteStudent();
  const cancelDeletion = useCancelDeletion();
  const enrollDelete = useAdminEnrollDelete();
  const enrollComplete = useAdminEnrollComplete();

  // ── Alt aksiyon barindan state ─────────────────────────────────────────────
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  // ── Sifre sifirlama ──────────────────────────────────────────────────────────
  const [pwResetModal, setPwResetModal] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [pwResetting, setPwResetting] = useState(false);

  // ── Pasife al / Aktife al (account_status) ────────────────────────────────────
  // Backend gerekce ZORUNLU kilar (account_status varsa account_status_reason
  // da olmali, bkz. routes/admin.py::admin_update_student_profile "ÜÇ
  // SERTLEŞTİRME" bloğu) — hem pasife alirken hem aktife alirken, AdminKvkkRequests.js
  // ile ayni desen. "Engelle" (PUT /admin/users/{id}/ban) hicbir auth kapisinda
  // kontrol edilmedigi icin islevsizdi, bu aksiyon onun yerine gecti.
  const [statusModal, setStatusModal] = useState(false);
  const [statusReason, setStatusReason] = useState('');
  const [statusSubmitting, setStatusSubmitting] = useState(false);

  // ── Seviye atama (cabin_crew_level / english_level) — gerekce zorunlu ────────
  // Panel sadelestirme Tur 2 (2026-09-29): web seviye tespit sinavi kaldirildi,
  // seviye artik yalnizca admin tarafindan elle atanir. PUT /admin/students/{id}/level
  // body {cabin_crew_level?, english_level?, reason} — degismeyen alan gonderilmez.
  const [levelModal, setLevelModal] = useState(false);
  const [levelCabin, setLevelCabin] = useState('');
  const [levelEnglish, setLevelEnglish] = useState('');
  const [levelReason, setLevelReason] = useState('');
  const [levelSubmitting, setLevelSubmitting] = useState(false);

  // ── Kabin programi kaydi (yalnizca role=student) ──────────────────────────
  // GET /admin/students/{id}/cabin-crew-enrollment -> {enrollment: {...}|null}
  // POST .../cabin-crew-enrollment -> kayit acar (notes opsiyonel)
  // POST .../cabin-crew-enrollment/end -> kaydi sonlandirir (reason zorunlu)
  const [cabinEnrollment, setCabinEnrollment] = useState(null);
  const [cabinEnrollModal, setCabinEnrollModal] = useState(false);
  const [cabinEnrollNotes, setCabinEnrollNotes] = useState('');
  const [cabinEnrollSubmitting, setCabinEnrollSubmitting] = useState(false);
  const [cabinEndModal, setCabinEndModal] = useState(false);
  const [cabinEndReason, setCabinEndReason] = useState('');
  const [cabinEndSubmitting, setCabinEndSubmitting] = useState(false);

  const handleDownloadPdf = async () => {
    if (!userId || pdfDownloading) return;
    setPdfDownloading(true);
    try {
      const res = await axios.get(
        `${API}/admin/students/${userId}/kayit-formu-pdf`,
        { headers: authHeaders(), responseType: 'blob' }
      );
      const kodu = detail?.user?.ogrenci_kodu || userId;
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `kayit-formu-${kodu}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleImpersonate = async () => {
    if (!userId) return;
    setImpersonateLoading(true);
    try {
      // Impersonate endpoint tek-kullanimlik ticket doner ({ ticket, expires_in, typ }).
      // Admin oturumunu EZMEMEK icin token'i bu sekmeye yazmiyoruz; ticket'i
      // /auto-login sayfasina yeni sekmede tasiyoruz (orada exchange edilip
      // ogrenci token'i o sekmenin localStorage'ina yazilir).
      const res = await axios.post(
        `${API}/admin/students/${userId}/impersonate`,
        {},
        { headers: authHeaders() }
      );
      const ticket = res.data?.ticket;
      if (ticket) {
        const url = `${window.location.origin}/auto-login?ticket=${encodeURIComponent(ticket)}`;
        window.open(url, '_blank', 'noopener');
        toast.success(lang === 'tr' ? 'Öğrenci paneli yeni sekmede açıldı' : 'Student panel opened in new tab');
      } else {
        toast.error(lang === 'tr' ? 'Giriş bileti alınamadı' : 'No login ticket received');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setImpersonateLoading(false);
    }
  };

  const handleResetPw = async () => {
    if (!newPw.trim() || !userId) return;
    setPwResetting(true);
    try {
      await axios.post(
        `${API}/admin/students/${userId}/reset-password`,
        { new_password: newPw },
        { headers: authHeaders() }
      );
      toast.success(t('admin.studentHub.pw_reset_success'));
      setPwResetModal(false);
      setNewPw('');
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setPwResetting(false);
    }
  };

  const isDeactivated = detail?.user?.account_status === 'deactivated';

  const openStatusModal = useCallback(() => {
    setStatusReason('');
    setStatusModal(true);
  }, []);

  const closeStatusModal = useCallback(() => {
    if (statusSubmitting) return;
    setStatusModal(false);
    setStatusReason('');
  }, [statusSubmitting]);

  const handleStatusSubmit = useCallback(async () => {
    const reason = statusReason.trim();
    if (!reason || !userId) return;
    setStatusSubmitting(true);
    try {
      await updateStudent.mutateAsync({
        account_status: isDeactivated ? 'active' : 'deactivated',
        account_status_reason: reason,
      });
      toast.success(
        isDeactivated
          ? t('admin.studentHub.action_reactivate')
          : t('admin.studentHub.action_deactivate')
      );
      setStatusModal(false);
      setStatusReason('');
      refetchDetail();
    } catch (err) {
      if (getApiErrorCode(err) === 'account_status_reason_required') {
        toast.error(
          lang === 'tr'
            ? 'Gerekçe boş olamaz — hesap durumu değiştirilmedi.'
            : 'The reason cannot be empty — the account status was not changed.'
        );
      } else {
        toast.error(getApiErrorMessage(err, lang));
      }
    } finally {
      setStatusSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusReason, userId, isDeactivated, updateStudent, refetchDetail]);

  const openLevelModal = useCallback(() => {
    setLevelCabin('');
    setLevelEnglish('');
    setLevelReason('');
    setLevelModal(true);
  }, []);

  const closeLevelModal = useCallback(() => {
    if (levelSubmitting) return;
    setLevelModal(false);
    setLevelCabin('');
    setLevelEnglish('');
    setLevelReason('');
  }, [levelSubmitting]);

  const handleLevelSubmit = useCallback(async () => {
    const reason = levelReason.trim();
    if (!reason || !userId) return;
    // Degismeyen alan gonderilmez — ikisi de bossa istek atilmaz.
    if (!levelCabin && !levelEnglish) return;
    setLevelSubmitting(true);
    try {
      const payload = { reason };
      if (levelCabin) payload.cabin_crew_level = levelCabin;
      if (levelEnglish) payload.english_level = levelEnglish;
      await axios.put(`${API}/admin/students/${userId}/level`, payload, { headers: authHeaders() });
      toast.success(t('admin.studentHub.level_saved'));
      setLevelModal(false);
      setLevelCabin('');
      setLevelEnglish('');
      setLevelReason('');
      refetchDetail();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setLevelSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelCabin, levelEnglish, levelReason, userId, authHeaders, refetchDetail]);

  const isStudentRole = detail?.user?.role === 'student';

  const fetchCabinEnrollment = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(
        `${API}/admin/students/${userId}/cabin-crew-enrollment`,
        { headers: authHeaders() }
      );
      setCabinEnrollment(res.data?.enrollment || null);
    } catch {
      setCabinEnrollment(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, authHeaders]);

  useEffect(() => {
    if (isStudentRole) {
      fetchCabinEnrollment();
    } else {
      setCabinEnrollment(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isStudentRole]);

  const openCabinEnrollModal = useCallback(() => {
    setCabinEnrollNotes('');
    setCabinEnrollModal(true);
  }, []);

  const closeCabinEnrollModal = useCallback(() => {
    if (cabinEnrollSubmitting) return;
    setCabinEnrollModal(false);
    setCabinEnrollNotes('');
  }, [cabinEnrollSubmitting]);

  const handleCabinEnrollSubmit = useCallback(async () => {
    if (!userId) return;
    setCabinEnrollSubmitting(true);
    try {
      const payload = {};
      const notes = cabinEnrollNotes.trim();
      if (notes) payload.notes = notes;
      await axios.post(
        `${API}/admin/students/${userId}/cabin-crew-enrollment`,
        payload,
        { headers: authHeaders() }
      );
      toast.success(t('admin.studentHub.cabin_enroll_success'));
      setCabinEnrollModal(false);
      setCabinEnrollNotes('');
      fetchCabinEnrollment();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCabinEnrollSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, cabinEnrollNotes, authHeaders, fetchCabinEnrollment]);

  const openCabinEndModal = useCallback(() => {
    setCabinEndReason('');
    setCabinEndModal(true);
  }, []);

  const closeCabinEndModal = useCallback(() => {
    if (cabinEndSubmitting) return;
    setCabinEndModal(false);
    setCabinEndReason('');
  }, [cabinEndSubmitting]);

  const handleCabinEndSubmit = useCallback(async () => {
    const reason = cabinEndReason.trim();
    if (!reason || !userId) return;
    setCabinEndSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/students/${userId}/cabin-crew-enrollment/end`,
        { reason },
        { headers: authHeaders() }
      );
      toast.success(t('admin.studentHub.cabin_end_success'));
      setCabinEndModal(false);
      setCabinEndReason('');
      fetchCabinEnrollment();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    } finally {
      setCabinEndSubmitting(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, cabinEndReason, authHeaders, fetchCabinEnrollment]);

  const handleDeleteAccount = useCallback(async () => {
    if (!userId) return;
    const confirmed = await confirm({
      title: t('admin.studentHub.kvkk_delete_title'),
      description: t('admin.studentHub.kvkk_delete_desc'),
      variant: 'danger',
      confirmLabel: t('admin.studentHub.kvkk_delete_btn'),
    });
    if (!confirmed) return;
    try {
      await deleteStudent.mutateAsync(userId);
      toast.success(t('admin.btn_delete') + ' OK');
      navigate('/admin/students');
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, deleteStudent, navigate]);

  const handleCancelDeletion = useCallback(async () => {
    if (!userId) return;
    const confirmed = await confirm({
      title: t('admin.studentHub.kvkk_cancel_title'),
      description: t('admin.studentHub.kvkk_cancel_desc'),
      variant: 'info',
      confirmLabel: t('admin.studentHub.kvkk_cancel_btn'),
    });
    if (!confirmed) return;
    try {
      await cancelDeletion.mutateAsync(userId);
      toast.success(t('admin.studentHub.kvkk_cancel_btn'));
      refetchDetail();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, cancelDeletion, refetchDetail]);

  // ── Action menu ───────────────────────────────────────────────────────────────
  const actionItems = detail
    ? [
        {
          label: t('admin.studentHub.action_reset_pw'),
          onClick: () => { setPwResetModal(true); setNewPw(''); },
        },
        {
          label: t('admin.studentHub.action_assign_level'),
          onClick: openLevelModal,
        },
        ...(isStudentRole
          ? [
              cabinEnrollment
                ? {
                    label: t('admin.studentHub.action_cabin_end'),
                    onClick: openCabinEndModal,
                    variant: 'destructive',
                  }
                : {
                    label: t('admin.studentHub.action_cabin_enroll'),
                    onClick: openCabinEnrollModal,
                  },
            ]
          : []),
        {
          label: isDeactivated
            ? t('admin.studentHub.action_reactivate')
            : t('admin.studentHub.action_deactivate'),
          onClick: openStatusModal,
          variant: isDeactivated ? 'default' : 'destructive',
        },
        ...(detail.user?.is_deletion_pending
          ? [{ label: t('admin.studentHub.action_cancel_deletion'), onClick: handleCancelDeletion }]
          : []),
        {
          label: t('admin.studentHub.action_delete'),
          onClick: handleDeleteAccount,
          variant: 'destructive',
        },
      ]
    : [];

  const user = detail?.user || {};
  const tabCounts = detail?.tab_counts || {};
  // Legacy `pasif` alanı VE yeni `account_status` — ikisinden biri pasifse
  // rozet Pasif gösterir. `pasif` bozulmadı (başka yüzeylerin hâlâ okuduğu
  // eski alan), account_status ise PUT /admin/students/{id}/profile'ın
  // gerçekten yazdığı kanonik alan.
  const isInactiveBadge = !!user.pasif || user.account_status === 'deactivated';

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <AdminLayout
      title={t('admin.studentDetail.title')}
      titleTr={t('admin.studentDetail.title')}
    >
      {/* Ust bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button
          onClick={() => navigate('/admin/students')}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white hover:bg-gray-50 text-sm font-bold transition-colors flex-shrink-0"
          data-testid="back-to-list-btn"
        >
          <ChevronLeft size={15} />
          {t('admin.studentDetail.back_to_list')}
        </button>

        <StudentSearchBar currentUserId={userId} onSelect={(id) => navigate(`/admin/students/${id}`)} />

        {/* Secili ogrencinin adi */}
        {user.name && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--brand-primary)]/8 border-2 border-[var(--brand-primary)]/20">
            <div className="w-6 h-6 rounded-full bg-[var(--brand-primary)] flex items-center justify-center overflow-hidden flex-shrink-0">
              {user.picture
                ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
                : <span className="text-white text-[9px] font-bold">{(user.name || '?')[0].toUpperCase()}</span>
              }
            </div>
            <span className="text-sm font-bold text-[var(--text-main)] truncate max-w-[180px]">{user.name}</span>
          </div>
        )}

        <div className="ml-auto flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => refetchDetail()}
            className="p-2 rounded-xl border-2 border-[var(--border-light)] bg-white hover:bg-gray-50 transition-colors"
            title={lang === 'tr' ? 'Yenile' : 'Refresh'}
          >
            <RefreshCw size={14} />
          </button>
          {actionItems.length > 0 && (
            <ActionMenu items={actionItems} align="end" />
          )}
        </div>
      </div>

      {/* Icerik alani */}
      {detailLoading ? (
        <div className="flex items-center justify-center h-64">
          <LoadingState />
        </div>
      ) : !detail ? (
        <div className="flex items-center justify-center h-64">
          <EmptyState
            title={lang === 'tr' ? 'Öğrenci bulunamadı' : 'Student not found'}
            icon={<User size={40} strokeWidth={1.5} />}
          />
        </div>
      ) : (
        <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 220px)' }}>

          {/* Kimlik ozet satiri */}
          <div className="flex items-center gap-3 mb-4 p-4 rounded-2xl border-2 border-[var(--border-light)] bg-white">
            <div className="w-12 h-12 rounded-full bg-[var(--brand-primary)] flex items-center justify-center overflow-hidden flex-shrink-0">
              {user.picture
                ? <img src={user.picture} alt="" className="w-full h-full object-cover" />
                : <span className="text-white font-bold text-lg">{(user.name || user.email || '?')[0].toUpperCase()}</span>
              }
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-xl font-bold truncate">{user.name || user.email || '-'}</h1>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                <span className="text-xs text-[var(--text-muted)]">{user.email}</span>
                {user.ogrenci_kodu && (
                  <span className="text-[10px] font-bold bg-gray-100 px-2 py-0.5 rounded-full text-[var(--text-muted)]">
                    #{user.ogrenci_kodu}
                  </span>
                )}
                <StatusBadge
                  variant={isInactiveBadge ? 'danger' : 'success'}
                  label={isInactiveBadge ? t('admin.studentHub.badge_inactive') : t('admin.studentHub.badge_active')}
                  dot
                />
                {user.banned && <StatusBadge variant="danger" label={lang === 'tr' ? 'Engelli' : 'Banned'} dot />}
                {user.is_deletion_pending && <StatusBadge variant="danger" label="KVKK" dot />}
                {user.cabin_crew_level && (
                  <StatusBadge
                    variant="info"
                    label={`${t('admin.studentHub.level_current_badge_cabin')}: ${t(`admin.studentHub.level_${user.cabin_crew_level}`) || user.cabin_crew_level}`}
                  />
                )}
                {user.english_level && (
                  <StatusBadge
                    variant="info"
                    label={`${t('admin.studentHub.level_current_badge_english')}: ${t(`admin.studentHub.level_${user.english_level}`) || user.english_level}`}
                  />
                )}
                {isStudentRole && (
                  <StatusBadge
                    variant={cabinEnrollment ? 'success' : 'neutral'}
                    label={cabinEnrollment
                      ? `${t('admin.studentHub.cabin_program_badge_label')}: ${t('admin.studentHub.cabin_program_registered')} (${new Date(cabinEnrollment.created_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')})`
                      : `${t('admin.studentHub.cabin_program_badge_label')}: ${t('admin.studentHub.cabin_program_not_registered')}`}
                  />
                )}
              </div>
            </div>
            {/* Hizli Yeni Satis butonu — her zaman gozukur */}
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => navigate(`/admin/students/${userId}/sale`)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 border-b-4 border-b-emerald-700 transition-colors flex-shrink-0"
              data-testid="quick-new-sale-btn"
            >
              <ShoppingCart size={15} />
              {lang === 'tr' ? 'Yeni Satış' : 'New Sale'}
            </motion.button>
          </div>

          {/* Sekme bari */}
          <div className="flex gap-1 mb-0 overflow-x-auto pb-px flex-shrink-0">
            {TABS.map(({ key, countField }) => {
              const count = countField ? (tabCounts[countField] ?? null) : null;
              const showBadge = typeof count === 'number' && count > 0;
              const isActive = activeTab === key;
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex items-center gap-1.5 px-3.5 py-2.5 text-xs font-bold whitespace-nowrap rounded-t-xl border-2 border-b-0 transition-colors ${
                    isActive
                      ? 'bg-white border-[var(--border-light)] text-[var(--brand-primary)]'
                      : 'bg-gray-50 border-transparent text-[var(--text-muted)] hover:text-[var(--text-main)] hover:bg-white'
                  }`}
                  data-testid={`detail-tab-${key}`}
                >
                  {t(`admin.studentDetail.${key}`)}
                  {showBadge && (
                    <span
                      title={key === 'tab_payments' ? t('admin.studentDetail.tab_payments_count_hint') : undefined}
                      className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold leading-none ${
                        isActive ? 'bg-[var(--brand-primary)] text-white' : 'bg-gray-200 text-[var(--text-muted)]'
                      }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sekme icerigi */}
          {/* NOT: AnimatePresence mode="wait" + exit animasyonu, sekme degisiminde
              eski icerigin cikis animasyonu (0.15s) bitene kadar yeni icerigi DOM'a
              sokmuyordu; agir sekmelerde (Muhasebe/Guvenlik) "ilk tikta yuklenmedi,
              cift tik gerekti" hissi veriyordu. Exit animasyonunu kaldirip icerigi
              anlik mount ediyoruz; key degisimi giris animasyonunu zaten tetikler. */}
          <div className="flex-1 bg-white border-2 border-[var(--border-light)] rounded-b-2xl rounded-tr-2xl p-5 overflow-y-auto mb-20">
            <div>
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.15 }}
              >
                {/* Kimlik Bilgileri = ProfileTab */}
                {activeTab === 'tab_kimlik' && (
                  <StudentProfileTab
                    detail={detail}
                    updateStudent={updateStudent}
                    onSuccess={() => refetchDetail()}
                  />
                )}

                {/* Egitimler = EnrollmentsTab + Yeni Satis */}
                {activeTab === 'tab_enrollments' && (
                  <div>
                    {/* Egitim yoksa buyuk cagri butonu */}
                    {(detail?.enrollments || []).length === 0 ? (
                      <div className="flex flex-col items-center justify-center gap-4 py-10">
                        <BookOpen size={48} className="text-[var(--text-muted)]" strokeWidth={1.2} />
                        <p className="text-base font-bold text-[var(--text-muted)]">
                          {lang === 'tr' ? 'Henüz eğitim kaydı yok' : 'No training enrollments yet'}
                        </p>
                        <motion.button
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => navigate(`/admin/students/${userId}/sale`)}
                          className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white text-sm font-bold hover:bg-emerald-600 border-b-4 border-b-emerald-700"
                          data-testid="empty-new-sale-btn"
                        >
                          <ShoppingCart size={16} />
                          {lang === 'tr' ? 'Yeni Satış Yap' : 'Make New Sale'}
                        </motion.button>
                      </div>
                    ) : (
                      <>
                        {/* Mevcut kayitlar varsa da ust kisminda buton goster */}
                        <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-[var(--border-light)]">
                          <span className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider">
                            {(detail?.enrollments || []).length} {lang === 'tr' ? 'eğitim kaydı' : 'enrollments'}
                          </span>
                          <motion.button
                            whileHover={{ scale: 1.03 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => navigate(`/admin/students/${userId}/sale`)}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 border-b-4 border-b-emerald-700"
                            data-testid="top-new-sale-btn"
                          >
                            <ShoppingCart size={13} />
                            {lang === 'tr' ? 'Yeni Satış' : 'New Sale'}
                          </motion.button>
                        </div>
                        <StudentEnrollmentsTab
                          detail={detail}
                          enrollDelete={enrollDelete}
                          enrollComplete={enrollComplete}
                          userId={userId}
                          onRefresh={refetchDetail}
                        />
                      </>
                    )}
                  </div>
                )}

                {/* Muhasebe = AccountingTab (4 alt sekme: Özet | Borç & Tahsilat | Senetler | Belgeler) */}
                {activeTab === 'tab_payments' && (
                  <AccountingTab
                    detail={detail}
                    userId={userId}
                    onNavigateToProfile={() => setActiveTab('tab_kimlik')}
                  />
                )}

                {/* Istatistikler = ActivityTab */}
                {activeTab === 'tab_activity' && (
                  <StudentActivityTab detail={detail} />
                )}

                {/* Sertifikalar */}
                {activeTab === 'tab_certificates' && (
                  <StudentCertificatesTab detail={detail} />
                )}

                {/* Gorusmeler */}
                {activeTab === 'tab_gorusmeler' && (
                  <StudentGorusmelerTab userId={userId} />
                )}

                {/* Sinav / Supervizyon */}
                {activeTab === 'tab_sinav' && (
                  <StudentSinavTab detail={detail} userId={userId} onRefresh={refetchDetail} />
                )}

                {/* KVKK */}
                {activeTab === 'tab_kvkk' && (
                  <StudentKvkkTab
                    detail={detail}
                    onCancelDeletion={handleCancelDeletion}
                    onDeleteAccount={handleDeleteAccount}
                    cancelDeletion={cancelDeletion}
                    deleteStudent={deleteStudent}
                  />
                )}

                {/* Guvenlik */}
                {activeTab === 'tab_security' && (
                  <StudentSecurityTab userId={userId} />
                )}
              </motion.div>
            </div>
          </div>
        </div>
      )}

      {/* Alt sabit aksiyon seridi */}
      {detail && !detailLoading && (
        <div className="sticky bottom-0 z-30 mt-6 -mx-1 bg-white/95 backdrop-blur rounded-t-2xl border-2 border-b-0 border-[var(--border-light)] px-4 py-3 flex items-center gap-2 flex-wrap justify-center shadow-[0_-4px_12px_rgba(0,0,0,0.06)]"
          data-testid="bottom-action-bar"
        >
          {/* SMS */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setSmsModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-emerald-500 bg-emerald-500 text-white text-xs font-bold hover:bg-emerald-600 border-b-4 border-b-emerald-700"
            data-testid="bottom-sms-btn"
          >
            <MessageSquare size={14} />
            {t('admin.studentHub.btn_send_sms')}
          </motion.button>

          {/* Kayit Formu PDF */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleDownloadPdf}
            disabled={pdfDownloading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-amber-500 bg-amber-500 text-white text-xs font-bold hover:bg-amber-600 border-b-4 border-b-amber-700 disabled:opacity-40"
            data-testid="bottom-pdf-btn"
          >
            <FileText size={14} />
            {pdfDownloading ? '...' : t('admin.studentHub.btn_pdf_form')}
          </motion.button>

          {/* Ogrenci Gibi Gir */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={handleImpersonate}
            disabled={impersonateLoading}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-sky-500 bg-sky-500 text-white text-xs font-bold hover:bg-sky-600 border-b-4 border-b-sky-700 disabled:opacity-40"
            data-testid="bottom-impersonate-btn"
          >
            <User size={14} />
            {impersonateLoading
              ? (lang === 'tr' ? 'Giriş...' : 'Loading...')
              : (lang === 'tr' ? 'Öğrenci Gibi Gir' : 'Login as Student')
            }
          </motion.button>

          {/* Sertifikalar sekmesine git */}
          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => setActiveTab('tab_certificates')}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl border-2 border-indigo-600 bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 border-b-4 border-b-indigo-800"
            data-testid="bottom-certs-btn"
          >
            <Award size={14} />
            {t('admin.studentDetail.tab_certificates')}
          </motion.button>
        </div>
      )}

      {/* Sifre sifirlama modal */}
      <AnimatePresence>
        {pwResetModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={() => !pwResetting && setPwResetModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="font-display text-lg font-bold mb-4">{t('admin.studentHub.pw_reset_title')}</h3>
              <input
                type="text"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder={t('admin.studentHub.pw_reset_new')}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold mb-4"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setPwResetModal(false)}
                  disabled={pwResetting}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold disabled:opacity-40"
                >
                  {t('admin.btn_cancel')}
                </button>
                <button
                  onClick={handleResetPw}
                  disabled={!newPw.trim() || pwResetting}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-b-4 border-[var(--brand-primary-shadow)] disabled:opacity-40"
                >
                  {pwResetting ? '...' : t('admin.studentHub.pw_reset_save')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Pasife al / Aktife al modal — gerekce zorunlu, bos gonderilemez */}
      <AnimatePresence>
        {statusModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={closeStatusModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="account-status-modal"
            >
              <h3 className="font-display text-lg font-bold mb-2">
                {isDeactivated
                  ? t('admin.studentHub.reactivate_confirm_title')
                  : t('admin.studentHub.deactivate_confirm_title')}
              </h3>
              <p className="text-sm text-[var(--text-muted)] mb-4">
                {isDeactivated
                  ? t('admin.studentHub.reactivate_confirm_desc')
                  : t('admin.studentHub.deactivate_confirm_desc')}
              </p>
              <label className="block text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
                {t('admin.studentHub.status_reason_label')}
              </label>
              <textarea
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                placeholder={t('admin.studentHub.status_reason_placeholder')}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold mb-4 resize-none"
                data-testid="account-status-reason-input"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={closeStatusModal}
                  disabled={statusSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold disabled:opacity-40"
                >
                  {t('admin.btn_cancel')}
                </button>
                <button
                  onClick={handleStatusSubmit}
                  disabled={!statusReason.trim() || statusSubmitting}
                  className={`flex-1 px-4 py-2.5 rounded-xl text-white text-sm font-bold border-b-4 disabled:opacity-40 ${
                    isDeactivated
                      ? 'bg-[var(--brand-primary)] border-[var(--brand-primary-shadow)]'
                      : 'bg-rose-500 border-rose-700'
                  }`}
                  data-testid="account-status-confirm-btn"
                >
                  {statusSubmitting
                    ? '...'
                    : isDeactivated
                    ? t('admin.studentHub.action_reactivate')
                    : t('admin.studentHub.action_deactivate')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Seviye atama modal — gerekce zorunlu, en az bir alan secilmeli */}
      <AnimatePresence>
        {levelModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={closeLevelModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="level-assign-modal"
            >
              <h3 className="font-display text-lg font-bold mb-2">
                {t('admin.studentHub.level_modal_title')}
              </h3>
              <p className="text-sm text-[var(--text-muted)] mb-4">
                {t('admin.studentHub.level_modal_desc')}
              </p>

              <label className="block text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
                {t('admin.studentHub.level_cabin_crew_label')}
              </label>
              <select
                value={levelCabin}
                onChange={(e) => setLevelCabin(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold mb-4 bg-white"
                data-testid="level-cabin-select"
              >
                <option value="">{t('admin.studentHub.level_keep_current')}{user.cabin_crew_level ? ` (${t(`admin.studentHub.level_${user.cabin_crew_level}`)})` : ''}</option>
                <option value="beginner">{t('admin.studentHub.level_beginner')}</option>
                <option value="intermediate">{t('admin.studentHub.level_intermediate')}</option>
                <option value="advanced">{t('admin.studentHub.level_advanced')}</option>
              </select>

              <label className="block text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
                {t('admin.studentHub.level_english_label')}
              </label>
              <select
                value={levelEnglish}
                onChange={(e) => setLevelEnglish(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold mb-4 bg-white"
                data-testid="level-english-select"
              >
                <option value="">{t('admin.studentHub.level_keep_current')}{user.english_level ? ` (${t(`admin.studentHub.level_${user.english_level}`)})` : ''}</option>
                <option value="beginner">{t('admin.studentHub.level_beginner')}</option>
                <option value="intermediate">{t('admin.studentHub.level_intermediate')}</option>
                <option value="advanced">{t('admin.studentHub.level_advanced')}</option>
              </select>

              <label className="block text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
                {t('admin.studentHub.level_reason_label')}
              </label>
              <textarea
                value={levelReason}
                onChange={(e) => setLevelReason(e.target.value)}
                placeholder={t('admin.studentHub.level_reason_placeholder')}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold mb-4 resize-none"
                data-testid="level-reason-input"
              />

              <div className="flex gap-2">
                <button
                  onClick={closeLevelModal}
                  disabled={levelSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold disabled:opacity-40"
                >
                  {t('admin.btn_cancel')}
                </button>
                <button
                  onClick={handleLevelSubmit}
                  disabled={!levelReason.trim() || (!levelCabin && !levelEnglish) || levelSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-b-4 border-[var(--brand-primary-shadow)] disabled:opacity-40"
                  data-testid="level-confirm-btn"
                >
                  {levelSubmitting ? '...' : t('admin.studentHub.level_save')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Kabin programina kaydet modal — not opsiyonel */}
      <AnimatePresence>
        {cabinEnrollModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={closeCabinEnrollModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="cabin-enroll-modal"
            >
              <h3 className="font-display text-lg font-bold mb-2">
                {t('admin.studentHub.cabin_enroll_title')}
              </h3>
              <p className="text-sm text-[var(--text-muted)] mb-4">
                {t('admin.studentHub.cabin_enroll_desc')}
              </p>

              {!user.cabin_crew_level && (
                <div className="mb-4 px-3 py-2.5 rounded-xl bg-amber-50 border-2 border-amber-200 text-xs font-semibold text-amber-800">
                  {t('admin.studentHub.cabin_enroll_no_level_hint')}
                </div>
              )}

              <label className="block text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
                {t('admin.studentHub.cabin_enroll_notes_label')}
              </label>
              <textarea
                value={cabinEnrollNotes}
                onChange={(e) => setCabinEnrollNotes(e.target.value)}
                placeholder={t('admin.studentHub.cabin_enroll_notes_placeholder')}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold mb-4 resize-none"
                data-testid="cabin-enroll-notes-input"
                autoFocus
              />

              <div className="flex gap-2">
                <button
                  onClick={closeCabinEnrollModal}
                  disabled={cabinEnrollSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold disabled:opacity-40"
                >
                  {t('admin.btn_cancel')}
                </button>
                <button
                  onClick={handleCabinEnrollSubmit}
                  disabled={cabinEnrollSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-[var(--brand-primary)] text-white text-sm font-bold border-b-4 border-[var(--brand-primary-shadow)] disabled:opacity-40"
                  data-testid="cabin-enroll-confirm-btn"
                >
                  {cabinEnrollSubmitting ? '...' : t('admin.studentHub.cabin_enroll_confirm')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Kabin program kaydini sonlandir modal — gerekce zorunlu */}
      <AnimatePresence>
        {cabinEndModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
            onClick={closeCabinEndModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              data-testid="cabin-end-modal"
            >
              <h3 className="font-display text-lg font-bold mb-2">
                {t('admin.studentHub.cabin_end_title')}
              </h3>
              <p className="text-sm text-[var(--text-muted)] mb-4">
                {t('admin.studentHub.cabin_end_desc')}
              </p>
              <label className="block text-xs font-bold text-[var(--text-muted)] uppercase tracking-wide mb-1.5">
                {t('admin.studentHub.cabin_end_reason_label')}
              </label>
              <textarea
                value={cabinEndReason}
                onChange={(e) => setCabinEndReason(e.target.value)}
                placeholder={t('admin.studentHub.cabin_end_reason_placeholder')}
                rows={3}
                className="w-full px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] focus:border-[var(--brand-primary)] focus:outline-none text-sm font-semibold mb-4 resize-none"
                data-testid="cabin-end-reason-input"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={closeCabinEndModal}
                  disabled={cabinEndSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl border-2 border-[var(--border-light)] text-sm font-bold disabled:opacity-40"
                >
                  {t('admin.btn_cancel')}
                </button>
                <button
                  onClick={handleCabinEndSubmit}
                  disabled={!cabinEndReason.trim() || cabinEndSubmitting}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-rose-500 text-white text-sm font-bold border-b-4 border-rose-700 disabled:opacity-40"
                  data-testid="cabin-end-confirm-btn"
                >
                  {cabinEndSubmitting ? '...' : t('admin.studentHub.cabin_end_confirm')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* SMS Modal */}
      <SmsSendModal
        open={smsModalOpen}
        onOpenChange={setSmsModalOpen}
        userId={userId}
        defaultPhone={detail?.user?.phone || detail?.user?.telefon || ''}
      />
    </AdminLayout>
  );
}

// ── Public export (ConfirmProvider sarmalayici) ───────────────────────────────
export default function AdminStudentDetailPage() {
  return (
    <ConfirmProvider>
      <StudentDetailInner />
    </ConfirmProvider>
  );
}
